import { Injectable } from '@angular/core';
import { NavController } from '@ionic/angular';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { AuthenticationFacade, goHomeAction, logoutAction } from '@symphony-solo/domain/authentication/src';
import { clearSelectCustomerGroupAction } from '@symphony-solo/domain/customer-groups/src';
import { clearLocationAction, LocationFacade } from '@symphony-solo/domain/location/src';
import { ScannerFacade } from '@symphony-solo/domain/scanner/src';
import { getProductDetailsSuccessAction } from '@symphony-solo/domain/select-product/src';
import { ToastFacade } from '@symphony-solo/domain/toast/src';
import { AlertService } from '@symphony-solo/shared/alert/src';
import { AppStateFailureBuilder, AppStateFailureItemWithErrorCode, FailureType } from '@symphony-solo/shared/common-state/src';
import { ApiResponseCode } from '@symphony-solo/shared/http/src';
import { PAYMENT_CONFIRMATION, TABS } from '@symphony-solo/shared/utils/src';
import { of } from 'rxjs';
import { catchError, map, switchMap, tap, withLatestFrom } from 'rxjs/operators';
import { TransactionProductBuilder } from '../builders/transaction-product.builder';
import { PaymentCardIdForRemoveTransaction } from '../consts';
import { TransactionFlowType } from '../enums/transaction-flow-type.enum';
import { TaxCalculatorService } from '../services';
import { TransactionGeneratorService } from '../services/transaction-generator.service';
import { TransactionService } from '../services/transaction.service';
import {
  addProductInTransactionAction,
  clearTransactionInformationAction,
  createTransactionAction,
  loadTransactionReceiptAction,
  loadTransactionReceiptErrorAction,
  loadTransactionReceiptSuccessAction,
  removeAllProductsFromTransactionAction,
  removeProductFromTransactionAction,
  removeTransactionAction,
  submitTransactionAction,
  submitTransactionErrorAction,
  submitTransactionSuccessAction,
} from './transaction.actions';
import { TransactionFacade } from './transaction.facade';
import { TransactionReceiptDto } from '../dtos';

@Injectable()
export class TransactionEffects {
  constructor(
    private action$: Actions,
    private transactionFacade: TransactionFacade,
    private navController: NavController,
    private taxCalculatorService: TaxCalculatorService,
    private alertService: AlertService,
    private transactionService: TransactionService,
    private transactionGeneratorService: TransactionGeneratorService,
    private authFacade: AuthenticationFacade,
    private toastFacade: ToastFacade,
    private locationFacade: LocationFacade,
    private scannerFacade: ScannerFacade
  ) {}

  protected getProductDetailsSuccess$ = createEffect(() =>
    this.action$.pipe(
      ofType(getProductDetailsSuccessAction),
      switchMap(({ productDetails }) => {
        const product = productDetails.product;
        const price = productDetails.prices[0];
        const productTransaction = new TransactionProductBuilder()
          .setId(product.id)
          .setName(product.name)
          .setQuantity(1)
          .setPrice(price)
          .build();
        return this.taxCalculatorService.getProductTransactionWithTax(productTransaction, product.taxRate).pipe(
          map((transactionProduct) => {
            return addProductInTransactionAction({ product: transactionProduct });
          })
        );
      })
    )
  );

  protected removeProductFromTransaction$ = createEffect(
    () =>
      this.action$.pipe(
        ofType(removeProductFromTransactionAction),
        withLatestFrom(this.transactionFacade.noOfItems$),
        tap(([_, noOfProducts]) => {
          if (noOfProducts === 0) {
            this.navController.navigateBack(TABS);
          }
        })
      ),
    { dispatch: false }
  );

  protected removeTransaction$ = createEffect(
    () =>
      this.action$.pipe(
        ofType(removeTransactionAction),
        tap(() => {
          const confirmationButton = {
            text: 'Yes',
            cssClass: 'okButton centerButton',
            handler: () => {
              this.transactionFacade.removeAllProductsFromTransaction(TransactionFlowType.Remove);
            },
          };
          const cancelButton = {
            text: 'No',
            cssClass: 'noButton centerButton',
            handler: () => {},
          };
          this.alertService.presentAlert(
            'Empty Basket?',
            'Are you sure you want to empty the basket? This cannot be reversed.',
            [cancelButton, confirmationButton],
            true
          );
        })
      ),
    { dispatch: false }
  );

  protected logout$ = createEffect(
    () =>
      this.action$.pipe(
        ofType(logoutAction),
        withLatestFrom(this.transactionFacade.isTransactionOpened$),
        tap(([_, isTransactionOpened]) => {
          if (!isTransactionOpened) {
            this.authFacade.logoutSuccess();
            return;
          }

          const confirmationButton = {
            text: 'Yes',
            cssClass: 'okButton centerButton',
            handler: () => {
              this.transactionFacade.removeAllProductsFromTransaction(TransactionFlowType.Logout);
            },
          };
          const cancelButton = {
            text: 'No',
            cssClass: 'noButton centerButton',
            handler: () => {},
          };
          this.alertService.presentAlert(
            'Logout',
            'Are you sure you want to log out? The current transaction will be removed.',
            [cancelButton, confirmationButton],
            true
          );
        })
      ),
    { dispatch: false }
  );

  protected goHome$ = createEffect(
    () =>
      this.action$.pipe(
        ofType(goHomeAction),
        withLatestFrom(this.transactionFacade.isTransactionOpened$),
        tap(([_, isTransactionOpened]) => {
          if (!isTransactionOpened) {
            this.authFacade.goHomeSuccess();
            return;
          }

          this.scannerFacade.closeIfScannerIsShown();

          const confirmationButton = {
            text: 'Yes',
            cssClass: 'okButton centerButton',
            handler: () => {
              this.transactionFacade.removeAllProductsFromTransaction(TransactionFlowType.GoHome);
            },
          };
          const cancelButton = {
            text: 'No',
            cssClass: 'noButton centerButton',
            handler: () => {
              this.scannerFacade.showTemporaryClosedScannerCamera();
            },
          };
          this.alertService.presentAlert(
            'Go Home',
            'Are you sure you want to exit Scan & Go? The current transaction will be removed.',
            [cancelButton, confirmationButton],
            true
          );
        })
      ),
    {
      dispatch: false,
    }
  );

  protected removeAllProductsFromTransaction$ = createEffect(() =>
    this.action$.pipe(
      ofType(removeAllProductsFromTransactionAction),
      switchMap((payload) => {
        return of(
          createTransactionAction({ paymentCardId: PaymentCardIdForRemoveTransaction, transactionFlow: payload.transactionFlowType })
        );
      })
    )
  );

  protected createTransaction$ = createEffect(() =>
    this.action$.pipe(
      ofType(createTransactionAction),
      switchMap((payload) => {
        return this.transactionGeneratorService
          .createTransaction()
          .pipe(map((transaction) => submitTransactionAction({ transaction, paymentCardId: payload.paymentCardId })));
      })
    )
  );

  protected submitTransaction$ = createEffect(() =>
    this.action$.pipe(
      ofType(submitTransactionAction),
      switchMap((payload) => {
        return this.transactionService.submitTransaction(payload.transaction, payload.paymentCardId).pipe(
          map((response) => {
            if (response.soloResponseCode === ApiResponseCode.Success) {
              return submitTransactionSuccessAction({ wordOfTheDay: response.value.securityWordOfTheDay });
            }

            let errorMessage = 'Failed to submit the transaction. Please try again.';
            switch (response.soloResponseCode) {
              case ApiResponseCode.Cant_Capture_Required_Funds: {
                errorMessage = `Can't capture required funds [error code: ${response.loyaltyResponseCode}]`;
                break;
              }
              case ApiResponseCode.Purse_Topup_Failed: {
                errorMessage = `Purse topup failed [error code: ${response.loyaltyResponseCode}]`;
                break;
              }
              case ApiResponseCode.Transaction_Already_Completed: {
                errorMessage = 'Transaction already completed';
                break;
              }
            }
            this.toastFacade.showToastErrorMessage(errorMessage);

            const failure = new AppStateFailureBuilder()
              .setFailureType(FailureType.API)
              .addFailure(new AppStateFailureItemWithErrorCode(errorMessage, response.soloResponseCode))
              .build();

            return submitTransactionErrorAction({ failure });
          }),
          catchError(() => {
            const errorMessage = 'Failed to submit the transaction. Please try again.';
            this.toastFacade.showToastErrorMessage(errorMessage);

            const failure = new AppStateFailureBuilder()
              .setFailureType(FailureType.API)
              .addFailure(new AppStateFailureItemWithErrorCode(errorMessage, ApiResponseCode.Server_Error))
              .build();

            return of(submitTransactionErrorAction({ failure }));
          })
        );
      })
    )
  );

  protected submitTransactionSuccess$ = createEffect(() =>
    this.action$.pipe(
      ofType(submitTransactionSuccessAction),
      withLatestFrom(this.transactionFacade.transactionFlowType$),
      switchMap(([_, transactionFlowType]) => {
        switch (transactionFlowType) {
          case TransactionFlowType.Remove:
            this.locationFacade.reAuthenticateLocation();
            break;
          case TransactionFlowType.Logout:
            this.authFacade.logoutSuccess();
            break;
          case TransactionFlowType.GoHome:
            this.authFacade.goHomeSuccess();
            break;
          case TransactionFlowType.Pay:
            this.navController.navigateRoot(PAYMENT_CONFIRMATION);
            break;
          default:
            throw new Error('Unknown transaction flow');
        }
        return of(clearSelectCustomerGroupAction(), clearLocationAction(), clearTransactionInformationAction());
      })
    )
  );

  protected submitTransactionError$ = createEffect(
    () => {
      return this.action$.pipe(
        ofType(submitTransactionErrorAction),
        tap(() => {
          this.scannerFacade.showTemporaryClosedScannerCamera();
        })
      );
    },
    { dispatch: false }
  );

  protected loadTransactionReceiptAction$ = createEffect(() =>
    this.action$.pipe(
      ofType(loadTransactionReceiptAction),
      switchMap((action) => {
        return this.transactionService.getTransactionReceipt(action.transactionId).pipe(
          map((transactionReceipt: TransactionReceiptDto) => {
            return loadTransactionReceiptSuccessAction({ transactionReceipt });
          }),
          catchError(() => {
            const failure = new AppStateFailureBuilder()
              .setFailureType(FailureType.API)
              .addFailure(new AppStateFailureItemWithErrorCode('Error on placing order', ApiResponseCode.Server_Error))
              .build();
            return of(loadTransactionReceiptErrorAction({ failure }));
          })
        );
      })
    )
  );
}
